/**
 * HelpOverlay.js
 *
 * Phase 13 (verification helper) — keyboard reference card shown on F1.
 *
 * Lists the 3D explorer's controls in a compact card so new players know
 * what every prompt + key does. Press F1 (or Esc) to dismiss.
 */

import React from 'react';

const ROWS = [
  { keys: ['W', 'A', 'S', 'D'], desc: 'Walk on foot' },
  { keys: ['Space'], desc: 'Jump · Boost (in ship)' },
  { keys: ['Shift'], desc: 'Sprint · Ship boost' },
  { keys: ['L'], desc: 'Launch / Land your ship' },
  { keys: ['Tab'], desc: 'Toggle pilot mode (in ship)' },
  { keys: ['Mouse / Drag'], desc: 'Look around' },
  { keys: ['LMB'], desc: 'Fire weapon (on foot)' },
  { keys: ['R'], desc: 'Reload weapon' },
  { keys: ['1', '2', '3'], desc: 'Switch weapon slots' },
  { keys: ['F'], desc: 'Start / stop mining drill' },
  { keys: ['B'], desc: 'Open build menu (Nanite structures)' },
  { keys: ['E'], desc: 'Interact (kiosks · NPC talk · anomaly scan · structures · enter city)' },
  { keys: ['Esc'], desc: 'Close overlays' },
  { keys: ['F1'], desc: 'Toggle this help' },
];

const PROMPTS = [
  { color: 'border-cyan-400/40 text-cyan-200', label: 'Kiosk', desc: 'Walk up to one of the 3 plaza buildings (Shop · Missions · Refuel) and press E.' },
  { color: 'border-teal-400/50 text-teal-200', label: 'Anomaly', desc: 'Teal floating pyramid on the surface — get within 6m and press E to scan.' },
  { color: 'border-fuchsia-400/40 text-fuchsia-200', label: 'Colonist', desc: 'A wandering NPC near the plaza — press E to hear their bark.' },
  { color: 'border-emerald-400/40 text-emerald-200', label: 'Structure', desc: 'Your own built structure (Crate, Refuel Pad, etc.) — press E to interact.' },
];

export function HelpOverlay({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
      data-testid="help-overlay"
      onClick={onClose}
    >
      <div
        className="w-[640px] max-w-full max-h-[88vh] overflow-y-auto rounded-2xl border border-cyan-400/40 bg-[#04101e]/95 shadow-[0_0_60px_rgba(24,210,255,0.25)] p-6"
        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-cyan-300/80">Controls</div>
            <h2 className="text-2xl text-cyan-100 mt-1">3D Explorer Reference</h2>
          </div>
          <button
            onClick={onClose}
            className="px-3 py-1 text-[10px] tracking-[0.3em] uppercase rounded bg-cyan-400/20 hover:bg-cyan-400/30 border border-cyan-400/40 text-cyan-200"
            data-testid="help-close-button"
          >
            F1 · Close
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mb-6">
          {ROWS.map((row) => (
            <div key={row.desc} className="flex items-baseline gap-3 text-sm">
              <div className="flex gap-1 flex-shrink-0 min-w-[110px]">
                {row.keys.map((k, i) => (
                  <kbd
                    key={i}
                    className="px-2 py-0.5 text-[10px] font-mono rounded border border-cyan-400/30 bg-cyan-400/10 text-cyan-200"
                  >
                    {k}
                  </kbd>
                ))}
              </div>
              <div className="text-slate-300 text-[12px]">{row.desc}</div>
            </div>
          ))}
        </div>

        <div className="mb-2 text-[10px] tracking-[0.4em] uppercase text-cyan-300/80">Prompts</div>
        <div className="space-y-2 mb-6">
          {PROMPTS.map((p) => (
            <div
              key={p.label}
              className={`px-3 py-2 rounded-lg border ${p.color} bg-black/30`}
            >
              <div className="text-[10px] tracking-[0.3em] uppercase opacity-80">{p.label}</div>
              <div className="text-[12px] text-slate-200 mt-0.5">{p.desc}</div>
            </div>
          ))}
        </div>

        <div className="text-[11px] text-slate-400 leading-relaxed border-t border-cyan-400/15 pt-3">
          Tip: anomalies (teal pyramids) glow on the radar with a pulsing halo
          when nearby — follow the arrow above the radar to the nearest one.
        </div>
      </div>
    </div>
  );
}

export default HelpOverlay;
